import { useState } from "react";
import { Plus, Trash2, Sparkles, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../../lib/api";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet transition-colors text-sm";
const nw = "bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet transition-colors text-sm";

function AIAssist({ type, onGenerated }) {
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (!topic.trim()) { toast.error("Escribe un tema"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/authoring/ai-generate", { type, topic: topic.trim(), count: Number(count) });
      onGenerated(data.config);
      toast.success("Contenido generado con IA. Revísalo y edítalo.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "La IA no pudo generar"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-violet/5 border border-violet/20 rounded-xl p-3 mb-4" data-testid="ai-assist">
      <div className="flex items-center gap-1.5 text-violet text-xs font-medium mb-2"><Sparkles className="w-3.5 h-3.5" /> Generar con IA (opcional)</div>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="ai-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Tema, p.ej. 'Seguridad e higiene'" className={`flex-1 min-w-[140px] ${nw}`} />
        <input data-testid="ai-count" type="number" min="2" max="10" value={count} onChange={(e) => setCount(e.target.value)} className={`w-16 shrink-0 ${nw}`} />
        <button data-testid="ai-generate-btn" onClick={gen} disabled={busy} className="shrink-0 rounded-lg bg-violet text-white text-sm px-4 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generar
        </button>
      </div>
      <p className="text-[11px] text-navy/40 mt-1.5">Reemplaza el contenido actual con una propuesta editable.</p>
    </div>
  );
}

function TriviaEditor({ config, onChange }) {
  const cfg = { seconds_per_q: 0, questions: [], ...config };
  const set = (patch) => onChange({ ...cfg, ...patch });
  const setQ = (i, patch) => set({ questions: cfg.questions.map((q, j) => j === i ? { ...q, ...patch } : q) });
  const addQ = () => set({ questions: [...cfg.questions, { q: "", options: ["", ""], answer: 0, points: 10 }] });
  const rmQ = (i) => set({ questions: cfg.questions.filter((_, j) => j !== i) });
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <label className="text-xs text-navy/50">Segundos por pregunta (0 = sin límite)</label>
        <input data-testid="trivia-spq" type="number" min="0" max="300" value={cfg.seconds_per_q} onChange={(e) => set({ seconds_per_q: Number(e.target.value) })} className={`w-24 shrink-0 ${nw}`} />
      </div>
      {cfg.questions.map((q, i) => (
        <div key={i} className="border border-navy/10 rounded-xl p-3 bg-white" data-testid={`trivia-q-${i}`}>
          <div className="flex items-start gap-2">
            <span className="text-[11px] w-5 h-5 rounded-full bg-violet/10 text-violet flex items-center justify-center shrink-0 mt-1.5">{i + 1}</span>
            <input value={q.q} onChange={(e) => setQ(i, { q: e.target.value })} placeholder="Pregunta" className={`${input} flex-1`} data-testid={`trivia-q-text-${i}`} />
            <button onClick={() => rmQ(i)} className="text-navy/30 hover:text-destructive mt-2"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="mt-2 space-y-1.5 pl-7">
            {q.options.map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <button onClick={() => setQ(i, { answer: oi })} title="Marcar como correcta" data-testid={`trivia-correct-${i}-${oi}`}
                  className={`w-6 h-6 rounded-full border shrink-0 flex items-center justify-center transition-colors ${q.answer === oi ? "bg-emerald-500 border-emerald-500 text-white" : "border-navy/20 text-transparent hover:border-emerald-400"}`}>
                  <Check className="w-3.5 h-3.5" />
                </button>
                <input value={opt} onChange={(e) => setQ(i, { options: q.options.map((o, j) => j === oi ? e.target.value : o) })} placeholder={`Opción ${oi + 1}`} className={`${input} flex-1`} data-testid={`trivia-opt-${i}-${oi}`} />
                {q.options.length > 2 && <button onClick={() => setQ(i, { options: q.options.filter((_, j) => j !== oi), answer: Math.min(q.answer, q.options.length - 2) })} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
            {q.options.length < 6 && <button onClick={() => setQ(i, { options: [...q.options, ""] })} className="text-xs text-violet hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Opción</button>}
          </div>
        </div>
      ))}
      <button onClick={addQ} data-testid="trivia-add-q" className="w-full rounded-lg border border-dashed border-navy/20 py-2.5 text-sm text-navy/55 hover:border-violet hover:text-violet inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Añadir pregunta</button>
    </div>
  );
}

function MemoramaEditor({ config, onChange }) {
  const cfg = { pairs: [], ...config };
  const set = (patch) => onChange({ ...cfg, ...patch });
  const setP = (i, patch) => set({ pairs: cfg.pairs.map((p, j) => j === i ? { ...p, ...patch } : p) });
  return (
    <div className="space-y-2">
      <p className="text-xs text-navy/45">Cada par se convierte en dos cartas para emparejar (concepto ↔ definición).</p>
      {cfg.pairs.map((p, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`memo-pair-${i}`}>
          <span className="text-[11px] w-5 h-5 rounded-full bg-violet/10 text-violet flex items-center justify-center shrink-0">{i + 1}</span>
          <input value={p.a} onChange={(e) => setP(i, { a: e.target.value })} placeholder="Concepto" className={`${input} flex-1`} data-testid={`memo-a-${i}`} />
          <span className="text-navy/30">↔</span>
          <input value={p.b} onChange={(e) => setP(i, { b: e.target.value })} placeholder="Definición" className={`${input} flex-1`} data-testid={`memo-b-${i}`} />
          <button onClick={() => set({ pairs: cfg.pairs.filter((_, j) => j !== i) })} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {cfg.pairs.length < 12 && <button onClick={() => set({ pairs: [...cfg.pairs, { a: "", b: "" }] })} data-testid="memo-add-pair" className="w-full rounded-lg border border-dashed border-navy/20 py-2.5 text-sm text-navy/55 hover:border-violet hover:text-violet inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Añadir par</button>}
    </div>
  );
}

function EscapeEditor({ config, onChange }) {
  const cfg = { intro: "", success: "", stages: [], ...config };
  const set = (patch) => onChange({ ...cfg, ...patch });
  const setS = (i, patch) => set({ stages: cfg.stages.map((s, j) => j === i ? { ...s, ...patch } : s) });
  return (
    <div className="space-y-3">
      <div><label className="text-xs text-navy/50 block mb-1">Historia de introducción</label>
        <textarea value={cfg.intro} onChange={(e) => set({ intro: e.target.value })} rows={2} placeholder="Ambientación del escape…" className={input} data-testid="escape-intro-input" /></div>
      {cfg.stages.map((s, i) => (
        <div key={i} className="border border-navy/10 rounded-xl p-3 bg-white space-y-2" data-testid={`escape-stage-${i}`}>
          <div className="flex items-center gap-2">
            <span className="text-[11px] w-5 h-5 rounded-full bg-violet/10 text-violet flex items-center justify-center shrink-0">{i + 1}</span>
            <input value={s.title} onChange={(e) => setS(i, { title: e.target.value })} placeholder="Título del reto" className={`${input} flex-1`} data-testid={`escape-title-${i}`} />
            <button onClick={() => set({ stages: cfg.stages.filter((_, j) => j !== i) })} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
          <textarea value={s.prompt} onChange={(e) => setS(i, { prompt: e.target.value })} rows={2} placeholder="Acertijo / pregunta" className={input} data-testid={`escape-prompt-${i}`} />
          <div className="flex gap-2">
            <input value={s.answer} onChange={(e) => setS(i, { answer: e.target.value })} placeholder="Respuesta correcta" className={`${input} flex-1`} data-testid={`escape-answer-${i}`} />
            <input value={s.hint || ""} onChange={(e) => setS(i, { hint: e.target.value })} placeholder="Pista (opcional)" className={`${input} flex-1`} data-testid={`escape-hint-${i}`} />
          </div>
        </div>
      ))}
      {cfg.stages.length < 12 && <button onClick={() => set({ stages: [...cfg.stages, { title: "", prompt: "", answer: "", hint: "" }] })} data-testid="escape-add-stage" className="w-full rounded-lg border border-dashed border-navy/20 py-2.5 text-sm text-navy/55 hover:border-violet hover:text-violet inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Añadir reto</button>}
      <div><label className="text-xs text-navy/50 block mb-1">Mensaje de victoria</label>
        <input value={cfg.success} onChange={(e) => set({ success: e.target.value })} placeholder="¡Escapaste!" className={input} data-testid="escape-success-input" /></div>
    </div>
  );
}

export default function GameConfigEditor({ type, config, onChange, showAI = true }) {
  return (
    <div data-testid="game-config-editor">
      {showAI && <AIAssist type={type} onGenerated={onChange} />}
      {type === "trivia" && <TriviaEditor config={config} onChange={onChange} />}
      {type === "memorama" && <MemoramaEditor config={config} onChange={onChange} />}
      {type === "escape" && <EscapeEditor config={config} onChange={onChange} />}
    </div>
  );
}
